/**
 * El foco al que pertenece un muestreo, para decirlo en SU ficha.
 *
 * La ficha del muestreo (`ScoutingSessionDetailDrawer`) no decía que el parte
 * estaba dentro de un foco, ni dejaba ir a él: desde Plagas no había forma de
 * saber a qué foco pertenecía (Pablo, 24-09-2026). Aquí solo lo que cabe en una
 * fila —número, plaga, estado y cuántos muestreos— y dónde está (nave y
 * ubicación) para abrirlo en el mapa. La ficha entera del foco sigue siendo
 * `FocusSheet`.
 *
 * El estado es el del muestreo más reciente del foco, con la MISMA regla que
 * el mapa y la ficha del foco (`stateOf`): manda el más reciente (F7).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocationScope } from "@/hooks/useLocationScope";
import { claveAlcance, enAlcance } from "@/lib/alcance";
import { stateOf } from "@/hooks/useFocusTimeline";
import type { LineState } from "@/lib/health/farmHealthModel";

export interface SessionFocusInfo {
  id: string;
  focusNumber: number;
  pestName: string;
  lineNumber: number | null;
  greenhouseId: string;
  locationId: string | null;
  state: LineState;
  samplings: number;
}

export function useSessionFocus(focusId: string | null | undefined) {
  const { company } = useAuth();
  const { allowedLocationIds, excludedBlockIds, loading: scopeLoading } = useLocationScope();
  return useQuery({
    queryKey: ["session-focus", focusId, company?.id, claveAlcance(allowedLocationIds, excludedBlockIds)],
    enabled: !!focusId && !!company?.id && !scopeLoading,
    staleTime: 15_000,
    queryFn: async (): Promise<SessionFocusInfo | null> => {
      const [focusRes, membersRes] = await Promise.all([
        supabase
          .from("pest_foci")
          .select("id, focus_number, pest_name_snapshot, line_number, greenhouse_id, block_id, greenhouse:greenhouses(location_id)")
          .eq("id", focusId!)
          .eq("company_id", company!.id)
          .maybeSingle(),
        // Miembros = lo que la ficha del foco cuenta: confirmados y revisiones limpias.
        supabase
          .from("pest_scouting_sessions")
          .select("review_status, worker_resolved_at, linked_task_id", { count: "exact" })
          .eq("focus_id", focusId!)
          .eq("company_id", company!.id)
          .or("triage_status.eq.classified,and(triage_status.eq.dismissed,dismissal_reason.eq.clean_check)")
          .order("date", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(1),
      ]);
      if (focusRes.error) throw focusRes.error;
      if (!focusRes.data) return null;
      const f = focusRes.data as unknown as {
        id: string; focus_number: number; pest_name_snapshot: string; line_number: number | null;
        greenhouse_id: string; block_id: string; greenhouse: { location_id: string | null } | null;
      };
      // Fuera de su alcance, el foco no existe para él (la misma regla que `useFocusTimeline`).
      if (!enAlcance({ block_id: f.block_id, location_id: f.greenhouse?.location_id ?? null }, allowedLocationIds, excludedBlockIds)) return null;
      const latest = (membersRes.data?.[0] ?? null) as
        | { review_status: string; worker_resolved_at: string | null; linked_task_id: string | null }
        | null;

      let taskStatus: string | null = null;
      if (latest?.linked_task_id) {
        const { data: task } = await supabase.from("planner_tasks").select("status").eq("id", latest.linked_task_id).maybeSingle();
        taskStatus = (task as { status?: string } | null)?.status ?? null;
      }

      return {
        id: f.id,
        focusNumber: f.focus_number,
        pestName: f.pest_name_snapshot,
        lineNumber: f.line_number,
        greenhouseId: f.greenhouse_id,
        locationId: f.greenhouse?.location_id ?? null,
        state: latest ? stateOf(latest, taskStatus) : "no_data",
        samplings: membersRes.count ?? 0,
      };
    },
  });
}
